import React, { useState, useEffect } from 'react';
import { Save, ArrowLeft, Plus, Trash2, Sparkles, Loader2 } from 'lucide-react';
import { ExamPaper, Question } from './types';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { regenerateQuestion } from '../services/geminiService';

// Import mathlive for visual math editing
import 'mathlive';
import { RichMathEditor } from './RichMathEditor';

interface DedicatedAssessmentEditorProps {
  examPaper: ExamPaper;
  onSave: (updatedPaper: ExamPaper) => void;
  onCancel: () => void;
}

export function DedicatedAssessmentEditor({ examPaper, onSave, onCancel }: DedicatedAssessmentEditorProps) {
  const [paper, setPaper] = useState<ExamPaper>(examPaper);
  const [activeSection, setActiveSection] = useState(0);
  
  // AI Regeneration State
  const [activeAiQuestion, setActiveAiQuestion] = useState<number | null>(null);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isRegenerating, setIsRegenerating] = useState<number | null>(null);

  const handleUpdateQuestion = (sIndex: number, qIndex: number, field: string, value: any) => {
    const newPaper = { ...paper };
    (newPaper.sections[sIndex].questions[qIndex] as any)[field] = value;
    setPaper(newPaper);
  };

  const handleUpdateSubQuestion = (sIndex: number, qIndex: number, sqIndex: number, field: string, value: any) => {
    const newPaper = { ...paper };
    (newPaper.sections[sIndex].questions[qIndex].subQuestions![sqIndex] as any)[field] = value;
    setPaper(newPaper);
  };

  const handleRegenerate = async (qIndex: number, instructions: string) => {
    try {
      setIsRegenerating(qIndex);
      const originalQuestion = paper.sections[activeSection].questions[qIndex];
      const newQuestion = await regenerateQuestion({
        originalQuestion,
        instructions
      });
      
      const newPaper = { ...paper };
      newQuestion.id = originalQuestion.id; // Preserve ID
      newPaper.sections[activeSection].questions[qIndex] = newQuestion;
      setPaper(newPaper);
      setActiveAiQuestion(null);
      setAiPrompt('');
    } catch (error) {
      console.error("Error regenerating question:", error);
      alert("Failed to regenerate question. Please try again.");
    } finally {
      setIsRegenerating(null);
    }
  };

  return (
    <div className="space-y-6 pb-20 animate-in fade-in duration-300">
      <div className="flex flex-wrap items-center justify-between gap-3 relative z-10 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md p-4 border-b border-slate-200 dark:border-slate-800 rounded-b-xl shadow-sm mb-6">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4 min-w-0 flex-1">
          <Button variant="ghost" onClick={onCancel} className="gap-2">
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-bold text-slate-900 dark:text-white">Assessment Editor</h1>
            <input 
              value={paper.title}
              onChange={(e) => setPaper({...paper, title: e.target.value})}
              className="text-xs text-blue-600 bg-transparent border-none p-0 focus:ring-0 w-full font-medium"
              placeholder="Rename Assessment..."
            />
          </div>
        </div>
        <Button onClick={() => onSave(paper)} className="gap-2 bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto">
          <Save className="w-4 h-4" /> Save All Changes
        </Button>
      </div>

      {/* Horizontal Sections Bar */}
      <div className="px-4 mb-4">
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Sections</h3>
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {paper.sections.map((sec, i) => (
            <button
              key={i}
              onClick={() => setActiveSection(i)}
              className={`whitespace-nowrap px-4 py-2 rounded-lg transition-all text-sm flex items-center gap-2 ${
                activeSection === i 
                  ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 border text-blue-700 dark:text-blue-400 font-bold shadow-sm' 
                  : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800/50'
              }`}
            >
              {sec.name}
              <span className="bg-slate-100 dark:bg-slate-700 text-[10px] px-1.5 py-0.5 rounded-full">{sec.questions.length}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="px-4">
        {/* Main Editor Area */}
        <div className="w-full space-y-6">
          <Card className="border-slate-200 dark:border-slate-800 shadow-sm w-full">
            <CardHeader className="bg-slate-50/50 dark:bg-slate-800/20 border-b border-slate-100 dark:border-slate-800 pb-4">
              <CardTitle className="text-lg flex justify-between items-center">
                <Input 
                  value={paper.sections[activeSection].name}
                  onChange={(e) => {
                    const newPaper = { ...paper };
                    newPaper.sections[activeSection].name = e.target.value;
                    setPaper(newPaper);
                  }}
                  className="font-bold text-lg bg-transparent border-transparent hover:border-slate-200 focus:border-blue-500 focus:bg-white dark:focus:bg-slate-900 transition-all px-2 max-w-sm"
                />
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 space-y-4 sm:space-y-10">
              {paper.sections[activeSection].questions.map((q, qIndex) => (
                <div key={q.id} className="relative p-4 sm:p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold text-sm shrink-0">
                        {qIndex + 1}
                      </div>
                      <Label className="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wide">Question {qIndex + 1}</Label>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setActiveAiQuestion(activeAiQuestion === qIndex ? null : qIndex)}
                      className={`gap-1.5 transition-colors ${activeAiQuestion === qIndex ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' : 'text-slate-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20'}`}
                    >
                      <Sparkles className="w-4 h-4" />
                      {activeAiQuestion === qIndex ? 'Close AI' : 'AI Edit'}
                    </Button>
                  </div>

                  {/* AI Adjust Panel */}
                  {activeAiQuestion === qIndex && (
                    <div className="mb-6 p-4 bg-purple-50/50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-800/50 rounded-xl animate-in slide-in-from-top-2 duration-200">
                      <Label className="text-xs font-bold text-purple-700 dark:text-purple-400 uppercase block mb-3">Adjust with AI</Label>
                      <div className="flex flex-wrap gap-2 sm:gap-3">
                        <Input
                          value={aiPrompt}
                          onChange={(e) => setAiPrompt(e.target.value)}
                          placeholder="e.g., 'Make it harder', 'Use a real-world example', or leave blank to just regenerate."
                          className="flex-1 min-w-[200px] bg-white dark:bg-slate-900 border-purple-200 dark:border-purple-800 focus:border-purple-500 focus:ring-purple-500"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !isRegenerating) {
                              handleRegenerate(qIndex, aiPrompt || 'Regenerate');
                            }
                          }}
                        />
                        <Button 
                          onClick={() => handleRegenerate(qIndex, aiPrompt || 'Regenerate')}
                          disabled={isRegenerating === qIndex}
                          className="bg-purple-600 hover:bg-purple-700 text-white min-w-[120px] w-full sm:w-auto"
                        >
                          {isRegenerating === qIndex ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Working...</>
                          ) : (
                            <><Sparkles className="w-4 h-4 mr-2" /> Apply</>
                          )}
                        </Button>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleRegenerate(qIndex, 'Rewrite this question entirely to be fresh but test the exact same concept.')}
                          disabled={isRegenerating === qIndex}
                          className="h-6 text-[10px] text-purple-600 border-purple-200 hover:bg-purple-50 dark:border-purple-800 dark:hover:bg-purple-900/30"
                        >
                          Rewrite
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleRegenerate(qIndex, 'Make this question slightly harder and more analytical.')}
                          disabled={isRegenerating === qIndex}
                          className="h-6 text-[10px] text-purple-600 border-purple-200 hover:bg-purple-50 dark:border-purple-800 dark:hover:bg-purple-900/30"
                        >
                          Make Harder
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleRegenerate(qIndex, 'Simplify the wording of this question to be easier to understand.')}
                          disabled={isRegenerating === qIndex}
                          className="h-6 text-[10px] text-purple-600 border-purple-200 hover:bg-purple-50 dark:border-purple-800 dark:hover:bg-purple-900/30"
                        >
                          Simplify
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  <div className={`space-y-4 transition-opacity ${isRegenerating === qIndex ? 'opacity-50 pointer-events-none' : ''}`}>
                    {/* Full Width Question Text */}
                    <div className="w-full">
                      <div className="rounded-xl border border-slate-200 dark:border-slate-700 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition-all">
                        <RichMathEditor
                          value={q.text}
                          onChange={(val) => handleUpdateQuestion(activeSection, qIndex, 'text', val)}
                          placeholder="Type question text here..."
                        />
                      </div>
                    </div>

                    {/* Metadata Row */}
                    <div className="flex flex-wrap gap-2 sm:gap-4 items-center justify-end pt-2 border-t border-slate-100 dark:border-slate-800">
                      <div className="flex items-center gap-2">
                        <Label className="text-[10px] font-bold text-slate-500 uppercase">Type:</Label>
                        <select
                          value={q.type}
                          onChange={(e) => handleUpdateQuestion(activeSection, qIndex, 'type', e.target.value)}
                          className="p-1.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md text-xs outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="multiple-choice">Multiple Choice</option>
                          <option value="short-answer">Short Answer</option>
                          <option value="essay">Essay</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-[10px] font-bold text-slate-500 uppercase">Marks:</Label>
                        <Input
                          type="number"
                          value={q.marks}
                          onChange={(e) => handleUpdateQuestion(activeSection, qIndex, 'marks', parseInt(e.target.value) || 0)}
                          className="w-16 h-8 text-xs font-bold text-center"
                        />
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => {
                          const newPaper = { ...paper };
                          newPaper.sections[activeSection].questions.splice(qIndex, 1);
                          setPaper(newPaper);
                        }}
                        className="text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 h-8 w-8 ml-2"
                        title="Delete Question"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Options Array */}
                    {q.type === 'multiple-choice' && (!q.subQuestions || q.subQuestions.length === 0) && (
                      <div className="space-y-3 bg-slate-50 dark:bg-slate-800/30 p-4 rounded-xl border border-slate-100 dark:border-slate-800">
                        <Label className="text-xs font-bold text-slate-500 uppercase block mb-2">Options</Label>
                        {(q.options || []).map((opt, oIndex) => (
                          <div key={oIndex} className="flex flex-wrap gap-2 sm:gap-3 items-center bg-white dark:bg-slate-900 p-2 rounded-lg border border-slate-200 dark:border-slate-700">
                            <input 
                              type="radio" 
                              name={`correct-${q.id}`} 
                              checked={q.answer === opt}
                              onChange={() => handleUpdateQuestion(activeSection, qIndex, 'answer', opt)}
                              className="w-4 h-4 text-blue-600 ml-2"
                            />
                            <span className="font-bold text-slate-400">{String.fromCharCode(65 + oIndex)}.</span>
                            <div className="flex-1">
                              <RichMathEditor
                                value={opt}
                                onChange={(val) => {
                                  const newOpts = [...(q.options || [])];
                                  newOpts[oIndex] = val;
                                  handleUpdateQuestion(activeSection, qIndex, 'options', newOpts);
                                  if (q.answer === opt) handleUpdateQuestion(activeSection, qIndex, 'answer', val);
                                }}
                                className="border-none shadow-none bg-transparent p-0"
                                placeholder={`Option ${String.fromCharCode(65 + oIndex)}`}
                              />
                            </div>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => {
                                const newOpts = [...(q.options || [])];
                                newOpts.splice(oIndex, 1);
                                handleUpdateQuestion(activeSection, qIndex, 'options', newOpts);
                              }}
                              className="text-red-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleUpdateQuestion(activeSection, qIndex, 'options', [...(q.options || []), `New Option`])}
                          className="mt-2 text-blue-600 border-blue-200 hover:bg-blue-50 dark:border-blue-800 dark:hover:bg-blue-900/20"
                        >
                          <Plus className="w-3.5 h-3.5 mr-1" /> Add Option
                        </Button>
                      </div>
                    )}

                    {/* Answer and Marking Scheme (For Non-MCQ or General Explanation) */}
                    {(!q.subQuestions || q.subQuestions.length === 0) && (
                      <div className="mt-4 p-4 bg-amber-50/30 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-900/30 rounded-xl space-y-4">
                        <Label className="text-xs font-bold text-amber-600 dark:text-amber-500 uppercase">Model Answer & Marking Scheme</Label>
                        
                        {q.type !== 'multiple-choice' && (
                          <div className="space-y-2">
                            <Label className="text-[10px] font-bold text-slate-500 uppercase">Model Answer</Label>
                            <RichMathEditor 
                                value={q.answer || ''} 
                                onChange={(v) => handleUpdateQuestion(activeSection, qIndex, 'answer', v)} 
                                placeholder="Expected answer..."
                                className="bg-white dark:bg-slate-900"
                            />
                          </div>
                        )}
                        
                        <div className="space-y-2">
                          <Label className="text-[10px] font-bold text-slate-500 uppercase">Explanation / Step-by-Step Marks</Label>
                          {(q.explanation || []).map((step, stepIndex) => (
                            <div key={stepIndex} className="flex flex-wrap gap-2 items-start bg-white dark:bg-slate-900 p-2 rounded-lg border border-slate-200 dark:border-slate-700">
                                <div className="mt-2 text-xs font-bold text-slate-400 w-12 text-center shrink-0">Step {stepIndex + 1}</div>
                                <div className="flex-1">
                                  <RichMathEditor 
                                      value={step} 
                                      onChange={(v) => {
                                          const newExp = [...(q.explanation || [])];
                                          newExp[stepIndex] = v;
                                          handleUpdateQuestion(activeSection, qIndex, 'explanation', newExp);
                                      }} 
                                      className="border-none shadow-none"
                                  />
                                </div>
                                <Button variant="ghost" size="icon" onClick={() => {
                                    const newExp = [...(q.explanation || [])];
                                    newExp.splice(stepIndex, 1);
                                    handleUpdateQuestion(activeSection, qIndex, 'explanation', newExp);
                                }} className="text-red-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                            </div>
                          ))}
                          <Button variant="outline" size="sm" onClick={() => {
                              handleUpdateQuestion(activeSection, qIndex, 'explanation', [...(q.explanation || []), '']);
                          }} className="text-xs mt-2">
                              <Plus className="w-3 h-3 mr-1" /> Add Step
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Sub Questions */}
                    {q.subQuestions && q.subQuestions.length > 0 && (
                      <div className="space-y-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                        <Label className="text-xs font-bold text-slate-500 uppercase block">Sub-questions</Label>
                        {q.subQuestions.map((sq, sqIndex) => (
                          <div key={sqIndex} className="p-3 sm:p-4 bg-slate-50 dark:bg-slate-800/30 rounded-xl border border-slate-200 dark:border-slate-700 space-y-4 overflow-hidden">
                            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                              <Input 
                                value={sq.id} 
                                onChange={(e) => handleUpdateSubQuestion(activeSection, qIndex, sqIndex, 'id', e.target.value)}
                                className="w-12 h-8 font-bold text-center bg-white dark:bg-slate-900 text-sm p-1" 
                              />
                              <Label className="text-xs font-bold text-slate-500">Sub-question</Label>
                            </div>
                            
                            <div className="w-full">
                              <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition-all">
                                <RichMathEditor
                                  value={sq.text}
                                  onChange={(val) => handleUpdateSubQuestion(activeSection, qIndex, sqIndex, 'text', val)}
                                  placeholder="Sub-question text..."
                                />
                              </div>
                            </div>
                            
                            <div className="flex flex-wrap gap-2 sm:gap-4 items-center justify-end pt-2 border-t border-slate-200 dark:border-slate-700/50">
                              <div className="flex items-center gap-2">
                                <Label className="text-[10px] font-bold text-slate-500 uppercase">Type:</Label>
                                <select
                                  value={sq.type}
                                  onChange={(e) => handleUpdateSubQuestion(activeSection, qIndex, sqIndex, 'type', e.target.value)}
                                  className="p-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md text-xs outline-none"
                                >
                                  <option value="multiple-choice">MCQ</option>
                                  <option value="short-answer">Short</option>
                                  <option value="essay">Essay</option>
                                </select>
                              </div>
                              <div className="flex items-center gap-2">
                                <Label className="text-[10px] font-bold text-slate-500 uppercase">Marks:</Label>
                                <Input 
                                  type="number" 
                                  value={sq.marks} 
                                  onChange={(e) => handleUpdateSubQuestion(activeSection, qIndex, sqIndex, 'marks', parseInt(e.target.value) || 0)}
                                  className="w-16 h-8 text-center bg-white dark:bg-slate-900 text-xs font-bold" 
                                />
                              </div>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => {
                                  const newSq = [...(q.subQuestions || [])];
                                  newSq.splice(sqIndex, 1);
                                  handleUpdateQuestion(activeSection, qIndex, 'subQuestions', newSq);
                                }}
                                className="text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 h-8 w-8 ml-2"
                                title="Delete Sub-question"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                            
                            {sq.type === 'multiple-choice' && (
                              <div className="mt-3 pl-2 pr-2 sm:pl-20 sm:pr-28 space-y-2">
                                {(sq.options || []).map((opt, oIndex) => (
                                  <div key={oIndex} className="flex flex-wrap gap-2 items-center bg-white dark:bg-slate-900 p-1.5 rounded-lg border border-slate-200 dark:border-slate-700">
                                    <input 
                                      type="radio" 
                                      name={`correct-sq-${q.id}-${sq.id}`} 
                                      checked={sq.answer === opt}
                                      onChange={() => handleUpdateSubQuestion(activeSection, qIndex, sqIndex, 'answer', opt)}
                                      className="w-3.5 h-3.5 text-blue-600 ml-2"
                                    />
                                    <span className="font-bold text-xs text-slate-400">{String.fromCharCode(65 + oIndex)}.</span>
                                    <div className="flex-1">
                                      <RichMathEditor
                                        value={opt}
                                        onChange={(val) => {
                                          const newOpts = [...(sq.options || [])];
                                          newOpts[oIndex] = val;
                                          handleUpdateSubQuestion(activeSection, qIndex, sqIndex, 'options', newOpts);
                                        }}
                                        className="border-transparent hover:border-slate-200 focus:border-blue-500 bg-transparent shadow-none p-0"
                                      />
                                    </div>
                                    <Button 
                                      variant="ghost" 
                                      size="icon"
                                      onClick={() => {
                                        const newOpts = [...(sq.options || [])];
                                        newOpts.splice(oIndex, 1);
                                        handleUpdateSubQuestion(activeSection, qIndex, sqIndex, 'options', newOpts);
                                      }}
                                      className="w-7 h-7 text-red-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                  </div>
                                ))}
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => handleUpdateSubQuestion(activeSection, qIndex, sqIndex, 'options', [...(sq.options || []), `New Option`])}
                                  className="h-7 text-xs text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                >
                                  <Plus className="w-3 h-3 mr-1" /> Add Option
                                </Button>
                              </div>
                            )}

                            {/* Sub-Question Answer and Marking Scheme */}
                            <div className="mt-4 p-3 bg-amber-50/20 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-900/30 rounded-xl space-y-3">
                              <Label className="text-[10px] font-bold text-amber-600 dark:text-amber-500 uppercase">Sub-question Marking Scheme</Label>
                              
                              {sq.type !== 'multiple-choice' && (
                                <div className="space-y-1">
                                  <Label className="text-[9px] font-bold text-slate-500 uppercase">Model Answer</Label>
                                  <RichMathEditor 
                                      value={sq.answer || ''} 
                                      onChange={(v) => handleUpdateSubQuestion(activeSection, qIndex, sqIndex, 'answer', v)} 
                                      placeholder="Expected answer..."
                                      className="bg-white dark:bg-slate-900"
                                  />
                                </div>
                              )}
                              
                              <div className="space-y-1">
                                <Label className="text-[9px] font-bold text-slate-500 uppercase">Explanation / Steps</Label>
                                {(sq.explanation || []).map((step, stepIndex) => (
                                  <div key={stepIndex} className="flex flex-wrap gap-2 items-start bg-white dark:bg-slate-900 p-2 rounded-lg border border-slate-200 dark:border-slate-700">
                                      <div className="mt-2 text-[10px] font-bold text-slate-400 w-10 text-center shrink-0">Step {stepIndex + 1}</div>
                                      <div className="flex-1">
                                        <RichMathEditor 
                                            value={step} 
                                            onChange={(v) => {
                                                const newExp = [...(sq.explanation || [])];
                                                newExp[stepIndex] = v;
                                                handleUpdateSubQuestion(activeSection, qIndex, sqIndex, 'explanation', newExp);
                                            }} 
                                            className="border-none shadow-none"
                                        />
                                      </div>
                                      <Button variant="ghost" size="icon" onClick={() => {
                                          const newExp = [...(sq.explanation || [])];
                                          newExp.splice(stepIndex, 1);
                                          handleUpdateSubQuestion(activeSection, qIndex, sqIndex, 'explanation', newExp);
                                      }} className="text-red-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 h-6 w-6">
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                  </div>
                                ))}
                                <Button variant="outline" size="sm" onClick={() => {
                                    handleUpdateSubQuestion(activeSection, qIndex, sqIndex, 'explanation', [...(sq.explanation || []), '']);
                                }} className="text-[10px] h-6 mt-1">
                                    <Plus className="w-3 h-3 mr-1" /> Add Step
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        <Button 
                          variant="outline" 
                          onClick={() => {
                            const newSq = [...(q.subQuestions || [])];
                            newSq.push({
                              id: String.fromCharCode(97 + newSq.length) + ')',
                              text: '',
                              type: 'short-answer',
                              marks: 1,
                              answer: '',
                              explanation: []
                            });
                            handleUpdateQuestion(activeSection, qIndex, 'subQuestions', newSq);
                          }}
                          className="w-full border-dashed text-slate-500 hover:text-blue-600 hover:border-blue-300 dark:hover:border-blue-700 bg-slate-50 dark:bg-slate-800/20"
                        >
                          <Plus className="w-4 h-4 mr-2" /> Add Sub-question
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <Button 
                variant="outline" 
                onClick={() => {
                  const newPaper = { ...paper };
                  newPaper.sections[activeSection].questions.push({
                    id: crypto.randomUUID(),
                    text: '',
                    type: 'short-answer',
                    marks: 1,
                    difficulty: 'Medium',
                    answer: '',
                    explanation: [],
                    subQuestions: []
                  });
                  setPaper(newPaper);
                }}
                className="w-full border-dashed border-2 py-8 text-slate-500 hover:text-blue-600 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-all font-bold mt-8"
              >
                <Plus className="w-5 h-5 mr-2" /> Add New Question
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
